import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Wrench, Boxes, Truck, RefreshCw, AlertTriangle,
  CheckCircle2, ArrowRight, Ghost, Lock, DollarSign, Scale, Receipt,
} from 'lucide-react';
import { superApi } from './SuperAdminAuth';

/**
 * Super-Admin → Data Cleanup
 * --------------------------
 * GUI front-end for the cross-tenant cleanup playbooks that previously
 * required SSH + a CLI script. Two playbooks today:
 *
 *   1. Inventory Dedupe — collapses duplicate `(company_id, sku)` rows
 *      in `db.inventory` (regression for the AFR8426 ping-pong bug).
 *   2. Courier RTO Returns — promotes returns auto-created from
 *      manager-override RTOs that landed before the courier_status
 *      mapping fix; moves them Pending → QC Pending and stamps the AWB.
 *
 * Both playbooks follow the same flow:
 *   • Click "Scan" → preview the list of affected rows (read-only,
 *     requires `super.system.view`).
 *   • Click "Apply" → confirmation modal → run the writes (requires
 *     `super.system.manage`). Every apply writes a `super_audit_log`
 *     entry.
 */

const Section = ({ title, description, Icon, children }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
    <div className="flex items-start gap-3 mb-4">
      <div className="p-2 rounded-lg bg-amber-500/10">
        <Icon className="w-5 h-5 text-amber-400" />
      </div>
      <div className="flex-1">
        <h2 className="text-base font-semibold text-white">{title}</h2>
        <p className="text-xs text-slate-400 mt-0.5">{description}</p>
      </div>
    </div>
    {children}
  </div>
);

const Pill = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-slate-800 text-slate-300',
    amber: 'bg-amber-500/15 text-amber-300',
    emerald: 'bg-emerald-500/15 text-emerald-300',
    rose: 'bg-rose-500/15 text-rose-300',
  };
  return (
    <span className={`inline-flex items-center text-[11px] font-mono px-2 py-0.5 rounded ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
};

// ─────────────────────────────  Inventory dedupe  ──────────────────────────
const InventoryDedupePanel = () => {
  const [groups, setGroups] = useState(null); // null = not scanned, [] = clean
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await superApi.get('/super/data-cleanup/inventory-dedupe');
      setGroups(r.data?.groups || []);
      if ((r.data?.groups || []).length === 0) {
        toast.success('Clean — no duplicate inventory rows found.');
      } else {
        toast.info(`Found ${r.data.groups.length} duplicate-SKU group${r.data.groups.length === 1 ? '' : 's'}.`);
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 404) {
        toast.error('Endpoint not deployed yet. Redeploy the backend so /api/super/data-cleanup/* goes live.');
      } else {
        toast.error(detail || `Scan failed (${status || 'network error'}).`);
      }
    } finally {
      setScanning(false);
    }
  };

  const apply = async () => {
    if (!groups || groups.length === 0) return;
    if (!window.confirm(
      `Merge ${groups.length} duplicate-SKU group${groups.length === 1 ? '' : 's'}?\n\n`
      + 'For each group: the canonical (track_inventory=true) row is kept, '
      + 'per-location stock is summed from the trustworthy row only, '
      + 'inventory_adjustments are repointed at the survivor, then the '
      + 'duplicate rows are deleted.\n\nThis action is logged to the '
      + 'super-admin audit log. Continue?'
    )) return;
    setApplying(true);
    try {
      const r = await superApi.post('/super/data-cleanup/inventory-dedupe');
      toast.success(
        `Merged ${r.data.groups_processed} group${r.data.groups_processed === 1 ? '' : 's'} `
        + `(${r.data.losers_deleted} duplicate row${r.data.losers_deleted === 1 ? '' : 's'} deleted, `
        + `${r.data.adjustments_repointed} adjustment${r.data.adjustments_repointed === 1 ? '' : 's'} repointed).`
      );
      await scan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Section
      title="Inventory Dedupe"
      description="Collapses duplicate (company, sku) rows into one canonical row. Idempotent."
      Icon={Boxes}
    >
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={scan}
          disabled={scanning || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-50"
          data-testid="dedupe-scan-btn"
        >
          <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning…' : 'Scan'}
        </button>
        <button
          onClick={apply}
          disabled={!groups || groups.length === 0 || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="dedupe-apply-btn"
        >
          {applying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          {applying ? 'Applying…' : `Apply (${groups?.length || 0})`}
        </button>
      </div>

      {groups === null ? (
        <p className="text-xs text-slate-500">Click Scan to preview duplicate inventory rows across every tenant.</p>
      ) : groups.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4" /> No duplicate-SKU groups detected.
        </div>
      ) : (
        <div className="space-y-3 max-h-[420px] overflow-auto" data-testid="dedupe-preview-list">
          {groups.map((g, i) => (
            <div key={`${g.company_id}-${g.sku}-${i}`} className="bg-slate-950 border border-slate-800 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-sm text-slate-100 truncate" title={g.product_title}>
                    {g.product_title || '(no title)'}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Pill tone="amber">SKU: {g.sku}</Pill>
                    <Pill>company: {g.company_id?.slice(0, 8) || '—'}</Pill>
                    <Pill tone="rose">{g.row_count} rows</Pill>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">After merge</div>
                  <div className="text-lg font-semibold text-emerald-300 font-mono">{g.merged_total_available}</div>
                </div>
              </div>
              <div className="space-y-1">
                {g.rows.map((row, j) => (
                  <div key={row.id || j} className="flex items-center gap-2 text-xs font-mono">
                    {row.is_winner ? (
                      <Pill tone="emerald">winner</Pill>
                    ) : (
                      <Pill tone="rose">delete</Pill>
                    )}
                    <span className="text-slate-400">{row.id?.slice(0, 8)}…</span>
                    <span className={row.track_inventory ? 'text-emerald-300' : 'text-slate-500'}>
                      track={row.track_inventory ? 'on' : 'off'}
                    </span>
                    <span className={row.total_available < 0 ? 'text-rose-400' : 'text-slate-300'}>
                      avail={row.total_available}
                    </span>
                    {row.shopify_variant_id && (
                      <span className="text-slate-500">vid={row.shopify_variant_id}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

// ──────────────────────────  Courier RTO returns  ──────────────────────────
const CourierRtoPanel = () => {
  const [returns, setReturns] = useState(null);          // pending → promote
  const [missing, setMissing] = useState(null);          // no return → create
  const [bluedartFailed, setBluedartFailed] = useState(0); // Bluedart re-sync candidates
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);

  const totalToProcess = (returns?.length || 0) + (missing?.length || 0) + (bluedartFailed || 0);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await superApi.get('/super/data-cleanup/courier-rto-returns');
      const pending = r.data?.pending_promotion || r.data?.returns || [];
      const miss = r.data?.missing_returns || [];
      const bd = r.data?.bluedart_failed_count || 0;
      setReturns(pending);
      setMissing(miss);
      setBluedartFailed(bd);
      const total = pending.length + miss.length + bd;
      if (total === 0) {
        toast.success('Clean — no courier-RTO returns to promote, create, or Bluedart re-sync.');
      } else {
        toast.info(`${pending.length} promote · ${miss.length} create · ${bd} Bluedart re-sync.`);
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 404) {
        toast.error('Endpoint not deployed yet. Redeploy the backend so /api/super/data-cleanup/* goes live.');
      } else {
        toast.error(detail || `Scan failed (${status || 'network error'}).`);
      }
    } finally {
      setScanning(false);
    }
  };

  const apply = async () => {
    if (totalToProcess === 0) return;
    if (!window.confirm(
      `Process ${totalToProcess} courier-RTO record${totalToProcess === 1 ? '' : 's'}:\n`
      + `  • ${returns?.length || 0} Pending return${(returns?.length || 0) === 1 ? '' : 's'} → promote to delivered/in_transit/pickup_assigned\n`
      + `  • ${missing?.length || 0} order${(missing?.length || 0) === 1 ? '' : 's'} with no return doc → auto-create one (will appear under Returns → QC Pending for delivered RTOs)\n`
      + `  • ${bluedartFailed || 0} Bluedart Failed order${bluedartFailed === 1 ? '' : 's'} → re-sync tracking; new NewWaybillNo logic flips eligible ones to RTO Delivered\n\n`
      + 'Logged to super-admin audit log. Continue?'
    )) return;
    setApplying(true);
    try {
      const r = await superApi.post('/super/data-cleanup/courier-rto-returns');
      const promoted = r.data.promoted || 0;
      const created = r.data.created || 0;
      const bdResynced = r.data.bluedart_resynced || 0;
      const bdFlipped = r.data.bluedart_flipped || 0;
      toast.success(
        `Promoted ${promoted}, created ${created} · Bluedart re-synced ${bdResynced}, flipped to RTO ${bdFlipped}.`
      );
      await scan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Section
      title="Courier RTO Returns"
      description="Promotes 'Pending' courier-RTO returns to the correct lifecycle, AND auto-creates return docs for orders with terminal RTO courier_status that were synced before the auto-create hook was wired into Sync All."
      Icon={Truck}
    >
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={scan}
          disabled={scanning || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-50"
          data-testid="rto-scan-btn"
        >
          <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning…' : 'Scan'}
        </button>
        <button
          onClick={apply}
          disabled={totalToProcess === 0 || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="rto-apply-btn"
        >
          {applying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          {applying ? 'Applying…' : `Apply (${totalToProcess})`}
        </button>
      </div>

      {returns === null ? (
        <p className="text-xs text-slate-500">Click Scan to find courier-RTO returns to promote AND orders missing a return doc AND Bluedart-failed orders to re-sync.</p>
      ) : totalToProcess === 0 ? (
        <div className="flex items-center gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4" /> Nothing to do.
        </div>
      ) : (
        <div className="space-y-3" data-testid="rto-preview-list">
          {(bluedartFailed || 0) > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
                Bluedart Failed orders to re-sync ({bluedartFailed})
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                These orders ended in NDR/Failed before the Feb-2026 fix added <code className="text-slate-300">NewWaybillNo</code> extraction. Apply re-syncs each — any whose return parcel has been delivered will flip to RTO and feed into the create-return phase below.
              </p>
            </div>
          )}
          {(returns?.length || 0) > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
                Pending returns to promote ({returns.length})
              </div>
              <div className="space-y-2 max-h-[260px] overflow-auto">
                {returns.map((r, i) => (
                  <div key={r.return_id || i} className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs font-mono">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Pill tone="amber">{r.return_number || r.return_id?.slice(0, 8)}</Pill>
                        <span className="text-slate-400">order {r.order_number || '—'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-500">
                        <span className="text-rose-300">{r.current_status}</span>
                        <ArrowRight className="w-3 h-3" />
                        <span className="text-emerald-300">{r.new_status}</span>
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                      <span>courier_status={r.courier_status}</span>
                      {r.missing_awb && r.awb_to_stamp && (
                        <Pill tone="amber">stamps AWB {r.awb_to_stamp}</Pill>
                      )}
                      {r.courier_to_stamp && (
                        <span>courier={r.courier_to_stamp}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(missing?.length || 0) > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
                Orders with no return doc — will be auto-created ({missing.length})
              </div>
              <div className="space-y-2 max-h-[260px] overflow-auto">
                {missing.map((o, i) => (
                  <div key={o.order_id || i} className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs font-mono">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Pill tone="amber">{o.order_number || o.order_id?.slice(0, 8)}</Pill>
                        <span className="text-slate-400">{o.customer_name || '—'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-500">
                        <span className="text-rose-300">{o.courier_status}</span>
                        <ArrowRight className="w-3 h-3" />
                        <span className="text-emerald-300">create return</span>
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                      {o.awb_number && <span>AWB {o.awb_number}</span>}
                      {o.courier_type && <span>courier={o.courier_type}</span>}
                      {typeof o.items_count === 'number' && <span>{o.items_count} item(s)</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Section>
  );
};

// ──────────────────────  Inventory-Adjustment Orphans  ─────────────────────
const AdjustmentOrphansPanel = () => {
  const [data, setData] = useState(null); // null = not scanned
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await superApi.get('/super/data-cleanup/inventory-adjustments-orphans');
      setData(r.data || { total_orphans: 0 });
      if (!r.data?.total_orphans) {
        toast.success('Clean — every inventory_adjustments row carries a company_id.');
      } else {
        toast.info(`Found ${r.data.total_orphans} orphan adjustment row${r.data.total_orphans === 1 ? '' : 's'}.`);
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 404) {
        toast.error('Endpoint not deployed yet. Redeploy the backend so /api/super/data-cleanup/inventory-adjustments-orphans goes live.');
      } else {
        toast.error(detail || `Scan failed (${status || 'network error'}).`);
      }
    } finally {
      setScanning(false);
    }
  };

  const apply = async () => {
    if (!data || !data.total_orphans) return;
    if (!window.confirm(
      `Backfill company_id on ${data.total_orphans} orphan adjustment row${data.total_orphans === 1 ? '' : 's'}?\n\n`
      + 'Each row will be linked to its parent inventory.company_id via the stored inventory_id. '
      + 'Rows whose inventory_id is missing or no longer exists in db.inventory are skipped (cannot resolve a safe tenant).\n\n'
      + 'Idempotent — running this again on a clean DB is a no-op. '
      + 'Logged to super-admin audit log. Continue?'
    )) return;
    setApplying(true);
    try {
      const r = await superApi.post('/super/data-cleanup/inventory-adjustments-orphans');
      toast.success(
        `Healed ${r.data.healed} adjustment row${r.data.healed === 1 ? '' : 's'}`
        + (r.data.skipped_no_inventory_id ? `, skipped ${r.data.skipped_no_inventory_id} with no inventory_id` : '')
        + (r.data.skipped_inventory_row_missing ? `, skipped ${r.data.skipped_inventory_row_missing} where inventory row was deleted` : '')
        + '.'
      );
      await scan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  const total = data?.total_orphans || 0;
  const sample = data?.sample || [];
  const unresolvable = data?.unresolvable_inventory_ids || [];

  return (
    <Section
      title="Inventory-Adjustment Orphans"
      description="Backfills company_id on legacy inventory_adjustments rows. Created Feb-14-2026 after the stock-history cross-tenant leak fix — adjustments missing company_id were invisible to the rightful tenant under the new strict filter."
      Icon={Boxes}
    >
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={scan}
          disabled={scanning || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-50"
          data-testid="adj-orphans-scan-btn"
        >
          <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning…' : 'Scan'}
        </button>
        <button
          onClick={apply}
          disabled={!total || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="adj-orphans-apply-btn"
        >
          {applying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          {applying ? 'Applying…' : `Backfill (${total})`}
        </button>
      </div>

      {data === null ? (
        <p className="text-xs text-slate-500">Click Scan to count adjustment rows that are missing a company_id (and would be invisible to tenant views).</p>
      ) : total === 0 ? (
        <div className="flex items-center gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4" /> Every adjustment row is properly tenant-scoped.
        </div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-auto" data-testid="adj-orphans-preview-list">
          <div className="text-[11px] text-slate-400 font-mono mb-1">
            {data.scanned_in_preview} previewed · {data.resolvable_in_preview} resolvable from inventory{unresolvable.length ? ` · ${unresolvable.length} unresolvable inventory_ids` : ''}
          </div>
          {sample.map((r, i) => (
            <div key={r.id || i} className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs font-mono">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <Pill tone="amber">{r.sku || '(no sku)'}</Pill>
                  <span className="text-slate-400 truncate" title={r.reason}>{r.reason || '—'}</span>
                </div>
                <div className="flex items-center gap-1 text-slate-500 shrink-0">
                  <span className={r.change_quantity >= 0 ? 'text-emerald-300' : 'text-rose-300'}>
                    {r.change_quantity >= 0 ? '+' : ''}{r.change_quantity}
                  </span>
                </div>
              </div>
              <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                <span>inventory {r.inventory_id?.slice(0, 8) || '—'}</span>
                <ArrowRight className="w-3 h-3" />
                <span className={r.resolved_company_id ? 'text-emerald-300' : 'text-rose-300'}>
                  company {r.resolved_company_id ? r.resolved_company_id.slice(0, 8) : 'unresolvable'}
                </span>
                {r.user_name && <span>by {r.user_name}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

// ──────────────────────────────  Phantom Stock  ────────────────────────────
const PhantomStockPanel = () => {
  const [rows, setRows] = useState(null);   // null = not scanned, [] = clean
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);

  const totalUnits = (rows || []).reduce((s, r) => s + (r.stored_avail || 0), 0);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await superApi.get('/super/data-cleanup/phantom-stock');
      const list = r.data?.rows || [];
      setRows(list);
      if (list.length === 0) {
        toast.success('Clean — no phantom stock detected.');
      } else {
        toast.info(`Found ${list.length} SKU${list.length === 1 ? '' : 's'} with phantom stock (${list.reduce((s, x) => s + (x.stored_avail || 0), 0)} units total).`);
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 404) {
        toast.error('Endpoint not deployed yet. Redeploy the backend so /api/super/data-cleanup/phantom-stock goes live.');
      } else {
        toast.error(detail || `Scan failed (${status || 'network error'}).`);
      }
    } finally {
      setScanning(false);
    }
  };

  const apply = async () => {
    if (!rows || rows.length === 0) return;
    if (!window.confirm(
      `Zero-out top-level stock counters on ${rows.length} SKU${rows.length === 1 ? '' : 's'} `
      + `(${totalUnits} phantom units across ${rows.length} row${rows.length === 1 ? '' : 's'})?\n\n`
      + 'Each affected row has total_available > 0 but every location holds 0. '
      + 'After cleanup, total_available / total_quantity / quantity will all be 0. '
      + 'Cost, vendor mappings, track_inventory and locations[] remain untouched.\n\n'
      + 'Logged to super-admin audit log. Continue?'
    )) return;
    setApplying(true);
    try {
      const r = await superApi.post('/super/data-cleanup/phantom-stock');
      toast.success(
        `Cleared ${r.data.rows_zeroed} SKU${r.data.rows_zeroed === 1 ? '' : 's'} `
        + `(${r.data.units_cleared} phantom unit${r.data.units_cleared === 1 ? '' : 's'} removed).`
      );
      await scan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Section
      title="Phantom Stock"
      description="Zeros out legacy SKUs whose top-level Total Stock > 0 but every location holds 0. Caused by pre-Shopify imports / dropped-app rows that left a stale total_available in the inventory doc."
      Icon={Ghost}
    >
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={scan}
          disabled={scanning || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-50"
          data-testid="phantom-scan-btn"
        >
          <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning…' : 'Scan'}
        </button>
        <button
          onClick={apply}
          disabled={!rows || rows.length === 0 || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="phantom-apply-btn"
        >
          {applying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          {applying ? 'Applying…' : `Apply (${rows?.length || 0})`}
        </button>
      </div>

      {rows === null ? (
        <p className="text-xs text-slate-500">Click Scan to find SKUs whose stored Total Stock isn't backed by any per-location quantity.</p>
      ) : rows.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4" /> No phantom stock detected.
        </div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-auto" data-testid="phantom-preview-list">
          {rows.map((r, i) => (
            <div key={r.id || i} className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs font-mono">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <Pill tone="amber">{r.sku}</Pill>
                  <span className="text-slate-300 truncate" title={r.title}>{r.title || '(no title)'}</span>
                </div>
                <div className="flex items-center gap-1 text-slate-500 shrink-0">
                  <span className="text-rose-300">stored {r.stored_avail}</span>
                  <ArrowRight className="w-3 h-3" />
                  <span className="text-emerald-300">0</span>
                </div>
              </div>
              <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                <span>company {r.company_id?.slice(0, 8) || '—'}</span>
                <span>locations {r.loc_count} ({r.nonzero_loc_count} non-zero)</span>
                {r.shopify_variant_id && <span>vid {r.shopify_variant_id}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

// ─────────────────────  Stuck Reservations (Delivered orders)  ─────────────
const StuckReservationsPanel = () => {
  const [rows, setRows] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);

  const totalUnits = (rows || []).reduce((s, r) => s + (r.total_units_blocked || 0), 0);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await superApi.get('/super/data-cleanup/stuck-reservations');
      const list = r.data?.rows || [];
      setRows(list);
      if (list.length === 0) {
        toast.success('Clean — no stuck reservations on delivered/failed orders.');
      } else {
        toast.info(
          `Found ${list.length} order${list.length === 1 ? '' : 's'} with stuck reservations `
          + `(${list.reduce((s, x) => s + (x.total_units_blocked || 0), 0)} units phantom-blocked).`
        );
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        toast.error('Endpoint not deployed yet. Redeploy the backend so /api/super/data-cleanup/stuck-reservations goes live.');
      } else {
        toast.error(e.response?.data?.detail || `Scan failed (${status || 'network error'}).`);
      }
    } finally {
      setScanning(false);
    }
  };

  const apply = async () => {
    if (!rows || rows.length === 0) return;
    if (!window.confirm(
      `Release ${totalUnits} unit${totalUnits === 1 ? '' : 's'} blocked across `
      + `${rows.length} delivered/failed order${rows.length === 1 ? '' : 's'}?\n\n`
      + 'Delivered orders → commit reservations (drops physical stock + clears the block).\n'
      + 'Failed orders → release reservations (returns stock to sellable).\n\n'
      + 'Idempotent. Logged to super-admin audit log. Continue?'
    )) return;
    setApplying(true);
    try {
      const r = await superApi.post('/super/data-cleanup/stuck-reservations');
      toast.success(
        `Processed ${r.data.applied} order${r.data.applied === 1 ? '' : 's'}.`
        + (r.data.errors?.length ? ` ${r.data.errors.length} error${r.data.errors.length === 1 ? '' : 's'}.` : '')
      );
      await scan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Section
      title="Stuck Inventory Reservations"
      description="Releases inventory blocked by orders that are already Delivered or Failed. Caused by pre-May-2026 driver/courier flows that flipped the order to delivered without committing the reservation."
      Icon={Lock}
    >
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={scan}
          disabled={scanning || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-50"
          data-testid="stuck-res-scan-btn"
        >
          <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning…' : 'Scan'}
        </button>
        <button
          onClick={apply}
          disabled={!rows || rows.length === 0 || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="stuck-res-apply-btn"
        >
          {applying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          {applying ? 'Applying…' : `Apply (${rows?.length || 0})`}
        </button>
      </div>

      {rows === null ? (
        <p className="text-xs text-slate-500">Click Scan to find delivered/failed orders that are still blocking inventory.</p>
      ) : rows.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4" /> No stuck reservations detected.
        </div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-auto" data-testid="stuck-res-preview-list">
          {rows.map((r, i) => (
            <div key={`${r.opswarex_order_id}-${i}`} className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs font-mono">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <Pill tone={r.action === 'commit' ? 'emerald' : 'rose'}>
                    {r.action === 'commit' ? 'COMMIT' : 'RELEASE'}
                  </Pill>
                  <Pill>{r.order_number || r.opswarex_order_id?.slice(0, 8)}</Pill>
                  <Pill tone="slate">{r.channel_type}</Pill>
                  <span className="text-slate-300 truncate" title={r.customer_name}>{r.customer_name || ''}</span>
                </div>
                <div className="flex items-center gap-1 text-slate-500 shrink-0">
                  <span className="text-rose-300">{r.total_units_blocked} blocked</span>
                  <ArrowRight className="w-3 h-3" />
                  <span className="text-emerald-300">0</span>
                </div>
              </div>
              <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                <span>company {r.company_id?.slice(0, 8) || '—'}</span>
                <span>{r.reservation_count} reservation{r.reservation_count === 1 ? '' : 's'}</span>
                <span>status {r.fulfillment_status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

// ──────────────────  Payroll Advance/Loan Double-Deduction  ────────────────
const PayrollDoubleDeductionPanel = () => {
  const [rows, setRows] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);

  const totalShortPaid = (rows || []).reduce((s, r) => s + (r.delta || 0), 0);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await superApi.get('/super/data-cleanup/payroll-double-deduction');
      const list = r.data?.rows || [];
      setRows(list);
      if (list.length === 0) {
        toast.success('Clean — no payroll double-deductions found.');
      } else {
        toast.info(
          `Found ${list.length} payroll row${list.length === 1 ? '' : 's'} affected. `
          + `Total short-paid: ${(r.data.total_short_paid || 0).toFixed(2)}`
        );
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        toast.error('Endpoint not deployed yet. Redeploy the backend so /api/super/data-cleanup/payroll-double-deduction goes live.');
      } else {
        toast.error(e.response?.data?.detail || `Scan failed (${status || 'network error'}).`);
      }
    } finally {
      setScanning(false);
    }
  };

  const apply = async () => {
    if (!rows || rows.length === 0) return;
    if (!window.confirm(
      `Restore ${totalShortPaid.toFixed(2)} short-paid across `
      + `${rows.length} salary credit${rows.length === 1 ? '' : 's'}?\n\n`
      + 'Each affected salary credit on the employee ledger will be increased '
      + 'by (advance_deduction + loan_deduction). The standalone deduction '
      + 'entries are left untouched (they were correct), so the net ledger '
      + 'movement will now match the actual cash paid out.\n\n'
      + 'Idempotent — already-fixed rows are skipped via a marker field. '
      + 'Logged to super-admin audit log. Continue?'
    )) return;
    setApplying(true);
    try {
      const r = await superApi.post('/super/data-cleanup/payroll-double-deduction');
      toast.success(
        `Restored ${(r.data.total_short_paid_restored || 0).toFixed(2)} across `
        + `${r.data.rows_fixed} payroll row${r.data.rows_fixed === 1 ? '' : 's'}.`
      );
      await scan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Section
      title="Payroll Advance/Loan Double-Deduction"
      description="Fixes employee ledgers where the salary credit excluded advance/loan and the standalone deduction entry ALSO excluded it. Restores the short-paid amount on every affected salary credit."
      Icon={DollarSign}
    >
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={scan}
          disabled={scanning || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-50"
          data-testid="payroll-dd-scan-btn"
        >
          <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning…' : 'Scan'}
        </button>
        <button
          onClick={apply}
          disabled={!rows || rows.length === 0 || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="payroll-dd-apply-btn"
        >
          {applying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          {applying ? 'Applying…' : `Apply (${rows?.length || 0})`}
        </button>
      </div>

      {rows === null ? (
        <p className="text-xs text-slate-500">Click Scan to find historical salary credits affected by the double-deduction bug.</p>
      ) : rows.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4" /> No payroll double-deductions detected.
        </div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-auto" data-testid="payroll-dd-preview-list">
          {rows.map((r) => (
            <div key={r.ledger_entry_id} className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs font-mono">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <Pill tone="emerald">+{r.delta.toFixed(2)}</Pill>
                  <span className="text-slate-200">{r.user_name}</span>
                  <Pill tone="slate">{r.month}</Pill>
                </div>
                <div className="flex items-center gap-1 text-slate-500 shrink-0">
                  <span className="text-rose-300">{(r.old_amount || 0).toFixed(2)}</span>
                  <ArrowRight className="w-3 h-3" />
                  <span className="text-emerald-300">{(r.new_amount || 0).toFixed(2)}</span>
                </div>
              </div>
              <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                <span>advance {r.advance_deduction.toFixed(2)}</span>
                <span>loan {r.loan_deduction.toFixed(2)}</span>
                <span>company {r.company_id?.slice(0, 8) || '—'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

// ─────────────────  Cost-History Backfill (from historical POs)  ───────────
const CostHistoryBackfillPanel = () => {
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await superApi.get('/super/data-cleanup/cost-history-backfill');
      setPreview(r.data);
      if ((r.data?.net_to_create || 0) === 0) {
        toast.success('Cost history already up to date.');
      } else {
        toast.info(
          `Will create ${r.data.net_to_create} cost-history row${r.data.net_to_create === 1 ? '' : 's'} `
          + `across ${r.data.distinct_skus} SKU${r.data.distinct_skus === 1 ? '' : 's'} from ${r.data.pos_scanned} POs.`
        );
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        toast.error('Endpoint not deployed yet. Redeploy backend so /api/super/data-cleanup/cost-history-backfill goes live.');
      } else {
        toast.error(e.response?.data?.detail || `Scan failed (${status || 'network error'}).`);
      }
    } finally {
      setScanning(false);
    }
  };

  const apply = async () => {
    if (!preview || (preview.net_to_create || 0) === 0) return;
    if (!window.confirm(
      `Backfill ${preview.net_to_create} cost-history row${preview.net_to_create === 1 ? '' : 's'} `
      + `from ${preview.pos_scanned} historical purchase order${preview.pos_scanned === 1 ? '' : 's'}?\n\n`
      + 'Source: every (sku, po_id, unit_cost) triple in purchase_orders_v2.\n'
      + 'Idempotent — re-running skips rows already seeded.\n'
      + 'Logged to super-admin audit log. Continue?'
    )) return;
    setApplying(true);
    try {
      const r = await superApi.post('/super/data-cleanup/cost-history-backfill');
      toast.success(
        `Created ${r.data.applied} cost-history row${r.data.applied === 1 ? '' : 's'}.`
        + (r.data.skipped_already_seeded ? ` (${r.data.skipped_already_seeded} already seeded — skipped.)` : '')
      );
      await scan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Section
      title="Cost History Backfill"
      description="Imports cost-change rows for every historical PO line item so the inventory Cost-History modal shows vendor pricing back to day one — not just future POs."
      Icon={Receipt}
    >
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={scan}
          disabled={scanning || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-50"
          data-testid="cost-history-backfill-scan-btn"
        >
          <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning…' : 'Scan'}
        </button>
        <button
          onClick={apply}
          disabled={!preview || (preview.net_to_create || 0) === 0 || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="cost-history-backfill-apply-btn"
        >
          {applying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          {applying ? 'Backfilling…' : `Apply (${preview?.net_to_create || 0})`}
        </button>
      </div>

      {preview === null ? (
        <p className="text-xs text-slate-500">Click Scan to count how many cost-history rows would be created from historical POs.</p>
      ) : (preview.net_to_create || 0) === 0 ? (
        <div className="flex items-center gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4" /> Cost history already covers every historical PO.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">POs scanned</div>
              <div className="text-base text-slate-200">{preview.pos_scanned}</div>
            </div>
            <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Distinct SKUs</div>
              <div className="text-base text-slate-200">{preview.distinct_skus}</div>
            </div>
            <div className="bg-slate-950 border border-slate-800 rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Already seeded</div>
              <div className="text-base text-slate-500">{preview.already_seeded}</div>
            </div>
            <div className="bg-slate-950 border border-amber-500/30 rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-amber-400">Net to create</div>
              <div className="text-base text-amber-300 font-semibold">{preview.net_to_create}</div>
            </div>
          </div>
          {(preview.top_skus_preview || []).length > 0 && (
            <div className="space-y-1.5 max-h-[300px] overflow-auto" data-testid="cost-history-backfill-preview-list">
              {(preview.top_skus_preview || []).map((row) => (
                <div key={row.sku} className="bg-slate-950 border border-slate-800 rounded-lg p-2 text-xs font-mono flex items-center gap-2">
                  <Pill>{row.sku}</Pill>
                  <span className="text-slate-300 truncate flex-1" title={row.title}>{row.title}</span>
                  <span className="text-emerald-300 shrink-0">{row.events} events</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Section>
  );
};

// ─────────────────  Inventory Master ↔ Rack Drift heal  ────────────────────
const InventoryMasterRackDriftPanel = () => {
  const [rows, setRows] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);

  const totalRestore = (rows || []).reduce((s, r) => s + Math.max(0, r.drift_units || 0), 0);
  const totalRemove = (rows || []).reduce((s, r) => s + Math.max(0, -(r.drift_units || 0)), 0);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await superApi.get('/super/data-cleanup/inventory-master-rack-drift');
      const list = r.data?.rows || [];
      setRows(list);
      if (list.length === 0) {
        toast.success('Clean — every SKU\'s master matches its rack-adjustment ground truth.');
      } else {
        const restore = r.data.total_units_to_restore || 0;
        const remove = r.data.total_units_to_remove || 0;
        toast.info(
          `Found ${list.length} drifted SKU${list.length === 1 ? '' : 's'}: `
          + `+${restore} to restore, -${remove} to remove.`
        );
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        toast.error('Endpoint not deployed yet. Redeploy backend so /api/super/data-cleanup/inventory-master-rack-drift goes live.');
      } else {
        toast.error(e.response?.data?.detail || `Scan failed (${status || 'network error'}).`);
      }
    } finally {
      setScanning(false);
    }
  };

  const apply = async () => {
    if (!rows || rows.length === 0) return;
    if (!window.confirm(
      `Heal ${rows.length} drifted SKU${rows.length === 1 ? '' : 's'}?\n\n`
      + `Restore: +${totalRestore} units across SKUs whose master under-counted.\n`
      + `Remove:  -${totalRemove} units across SKUs whose master over-counted.\n\n`
      + 'Each affected inventory doc gets its master `total_available` (and '
      + 'mirror fields) rebuilt from the SUM of inventory_adjustments. '
      + 'Per-location splits are scaled proportionally so nothing collapses '
      + 'to zero unexpectedly.\n\n'
      + 'Logged to super-admin audit log with before/after numbers. '
      + 'Idempotent. Continue?'
    )) return;
    setApplying(true);
    try {
      const r = await superApi.post('/super/data-cleanup/inventory-master-rack-drift');
      toast.success(
        `Healed ${r.data.applied} SKU${r.data.applied === 1 ? '' : 's'}.`
        + (r.data.errors?.length ? ` ${r.data.errors.length} error${r.data.errors.length === 1 ? '' : 's'}.` : '')
      );
      await scan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Section
      title="Inventory Master ↔ Rack Drift"
      description="Rebuilds inventory.total_available from the SUM of every inventory_adjustments row for the SKU (the canonical ledger of stock movements). Fixes the AFR4267 symptom where Inventory Master shows Total Stock 28 / Available 2 because the master got out of sync with the rack-level audit trail."
      Icon={Scale}
    >
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={scan}
          disabled={scanning || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm disabled:opacity-50"
          data-testid="master-rack-drift-scan-btn"
        >
          <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
          {scanning ? 'Scanning…' : 'Scan'}
        </button>
        <button
          onClick={apply}
          disabled={!rows || rows.length === 0 || applying}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="master-rack-drift-apply-btn"
        >
          {applying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          {applying ? 'Healing…' : `Apply (${rows?.length || 0})`}
        </button>
      </div>

      {rows === null ? (
        <p className="text-xs text-slate-500">Click Scan to find SKUs whose master Available has drifted from the rack-adjustment ground truth.</p>
      ) : rows.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="w-4 h-4" /> No drift detected.
        </div>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-auto" data-testid="master-rack-drift-preview-list">
          {rows.map((r) => (
            <div key={r.inventory_id || r.sku} className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs font-mono">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <Pill tone={r.drift_units > 0 ? 'emerald' : 'rose'}>
                    {r.drift_units > 0 ? `+${r.drift_units}` : r.drift_units}
                  </Pill>
                  <Pill>{r.sku}</Pill>
                  <span className="text-slate-300 truncate max-w-md" title={r.product_name}>
                    {r.product_name}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-slate-500 shrink-0">
                  <span className="text-rose-300">master {r.master_total_available}</span>
                  <ArrowRight className="w-3 h-3" />
                  <span className="text-emerald-300">{r.expected_total_available}</span>
                </div>
              </div>
              <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                <span>rack-sum {r.rack_sum_ground_truth}</span>
                <span>blocked {r.currently_blocked}</span>
                <span>sellable-after-heal {r.expected_sellable_after_heal}</span>
                <span>{r.adjustment_rows} adj rows</span>
                <span>co {r.company_id?.slice(0, 8) || '—'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

// ────────────────────────────  Page wrapper  ────────────────────────────────
export const SuperDataCleanupPage = () => {
  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="super-data-cleanup">
      <div className="flex items-center gap-2 mb-1">
        <Wrench className="w-6 h-6 text-amber-400" />
        <h1 className="text-2xl font-semibold text-white">Data Cleanup</h1>
      </div>
      <p className="text-sm text-slate-400 mb-6">
        Cross-tenant fixes for known data drifts. Always Scan first to preview;
        the writes only happen on Apply.
      </p>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-6 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-200">
          These jobs operate across <strong>every tenant</strong> by default.
          Every Apply is logged to <code className="bg-slate-900 px-1 py-0.5 rounded">super_audit_log</code>.
          All jobs are idempotent — running them again on a clean DB does nothing.
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <CostHistoryBackfillPanel />
        <InventoryMasterRackDriftPanel />
        <StuckReservationsPanel />
        <PayrollDoubleDeductionPanel />
        <InventoryDedupePanel />
        <AdjustmentOrphansPanel />
        <PhantomStockPanel />
        <CourierRtoPanel />
      </div>
    </div>
  );
};

export default SuperDataCleanupPage;
